import React, { useEffect, useState } from "react";
import { useNavigate, NavLink, Routes, Route } from "react-router-dom";
import api from "../../lib/api";
import { LogOut, Package, ShoppingBag, Inbox, BarChart3, Plus, Pencil, Trash2, X } from "lucide-react";

function useAdmin() {
  const nav = useNavigate();
  useEffect(() => {
    api.get("/admin/me").catch(() => {
      localStorage.removeItem("earthorg_admin_token");
      nav("/admin/login");
    });
  }, [nav]);
}

export default function AdminApp() {
  useAdmin();
  const nav = useNavigate();
  const logout = () => { localStorage.removeItem("earthorg_admin_token"); nav("/admin/login"); };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-overline transition-colors ${
      isActive ? "bg-[#1A1A1A] text-[#FAF9F6]" : "text-[#4A4A4A] hover:bg-[#EAE6DB]"
    }`;

  return (
    <div data-testid="admin-dashboard" className="pt-28 pb-24 max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <aside className="md:col-span-3">
          <p className="text-xs uppercase tracking-overline text-[#7A7A7A] mb-4 px-4">EarthOrg Admin</p>
          <nav className="border border-[#D5D1C5] bg-[#F5F2EA]">
            <NavLink end to="/admin" className={linkCls} data-testid="admin-nav-stats"><BarChart3 size={16}/> Overview</NavLink>
            <NavLink to="/admin/products" className={linkCls} data-testid="admin-nav-products"><Package size={16}/> Products</NavLink>
            <NavLink to="/admin/orders" className={linkCls} data-testid="admin-nav-orders"><ShoppingBag size={16}/> Orders</NavLink>
            <NavLink to="/admin/inquiries" className={linkCls} data-testid="admin-nav-inquiries"><Inbox size={16}/> Inquiries</NavLink>
            <button data-testid="admin-logout-btn" onClick={logout} className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-overline text-[#C84B31] hover:bg-[#EAE6DB] border-t border-[#D5D1C5]">
              <LogOut size={16}/> Logout
            </button>
          </nav>
        </aside>

        <main className="md:col-span-9">
          <Routes>
            <Route index element={<Stats/>}/>
            <Route path="products" element={<ProductsManager/>}/>
            <Route path="orders" element={<OrdersList/>}/>
            <Route path="inquiries" element={<InquiriesList/>}/>
          </Routes>
        </main>
      </div>
    </div>
  );
}

function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)); }, []);
  if (!s) return <p>Loading…</p>;
  const cards = [
    { l: "Products", v: s.products, t: "stat-products" },
    { l: "Orders", v: s.orders, t: "stat-orders" },
    { l: "Inquiries", v: s.inquiries, t: "stat-inquiries" },
    { l: "Revenue (USD)", v: `$${s.revenue_usd.toFixed(2)}`, t: "stat-revenue" },
  ];
  return (
    <div>
      <h2 className="font-serif-display text-4xl tracking-tight mb-8">Overview</h2>
      <div className="grid grid-cols-2 gap-6">
        {cards.map((c) => (
          <div key={c.l} data-testid={c.t} className="bg-[#F5F2EA] border border-[#D5D1C5] p-8">
            <p className="text-xs uppercase tracking-overline text-[#7A7A7A] block mb-4">{c.l}</p>
            <p className="font-serif-display text-5xl tracking-tighter text-[#C84B31] block">{c.v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const emptyProduct = {
  name:"", slug:"", category:"saffron", short_description:"", description:"", origin:"",
  price_usd: 0, moq_units:1, unit_label:"10g", image_url:"",
  gallery:[], tasting_notes:[], certifications:[], stock:100, featured:false, active:true,
};

function ProductsManager() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/products").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif-display text-4xl tracking-tight">Products</h2>
        <button data-testid="admin-new-product-btn" onClick={() => setEditing({ ...emptyProduct })} className="bg-[#C84B31] text-[#FAF9F6] px-5 py-3 uppercase tracking-overline text-xs hover:bg-[#A93F29] flex items-center gap-2">
          <Plus size={14}/> New
        </button>
      </div>

      <div className="border border-[#D5D1C5] divide-y divide-[#D5D1C5]">
        {list.map((p) => (
          <div key={p.id} data-testid={`admin-product-row-${p.slug}`} className="flex items-center gap-4 p-4 bg-[#FAF9F6]">
            <img src={p.image_url} alt="" className="w-14 h-14 object-cover bg-[#EAE6DB]"/>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{p.name}</p>
              <p className="text-xs text-[#7A7A7A] uppercase tracking-overline">{p.category} · ${p.price_usd} · stock {p.stock}</p>
            </div>
            <button data-testid={`admin-edit-${p.slug}`} onClick={() => setEditing(p)} className="p-2 hover:text-[#C84B31]"><Pencil size={16}/></button>
            <button data-testid={`admin-delete-${p.slug}`} onClick={() => del(p.id)} className="p-2 hover:text-[#C84B31]"><Trash2 size={16}/></button>
          </div>
        ))}
      </div>

      {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    ...initial,
    tasting_notes: (initial.tasting_notes || []).join(", "),
    certifications: (initial.certifications || []).join(", "),
  });
  const [err, setErr] = useState("");
  const isEdit = !!initial.id;

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    const payload = {
      ...f,
      price_usd: parseFloat(f.price_usd),
      moq_units: parseInt(f.moq_units),
      stock: parseInt(f.stock),
      tasting_notes: f.tasting_notes.split(",").map(s=>s.trim()).filter(Boolean),
      certifications: f.certifications.split(",").map(s=>s.trim()).filter(Boolean),
      gallery: f.gallery || [],
    };
    try {
      if (isEdit) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post(`/admin/products`, payload);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    }
  };

  const inp = "w-full bg-[#FAF9F6] border border-[#D5D1C5] px-3 py-2 outline-none focus:border-[#1A1A1A] text-sm";

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <div data-testid="admin-product-form" className="bg-[#FAF9F6] border border-[#D5D1C5] w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#D5D1C5]">
          <h3 className="font-serif-display text-3xl tracking-tight">{isEdit ? "Edit" : "New"} product</h3>
          <button onClick={onClose} data-testid="admin-product-form-close"><X size={20}/></button>
        </div>
        <form onSubmit={save} className="p-6 grid grid-cols-2 gap-4">
          <L l="Name"><input data-testid="pf-name" required value={f.name} onChange={e=>setF({...f,name:e.target.value})} className={inp}/></L>
          <L l="Slug"><input data-testid="pf-slug" required value={f.slug} onChange={e=>setF({...f,slug:e.target.value})} className={inp}/></L>
          <L l="Category">
            <select data-testid="pf-category" value={f.category} onChange={e=>setF({...f,category:e.target.value})} className={inp}>
              <option value="saffron">Saffron</option><option value="honey">Honey</option><option value="spices">Spices</option>
            </select>
          </L>
          <L l="Origin"><input data-testid="pf-origin" value={f.origin} onChange={e=>setF({...f,origin:e.target.value})} className={inp}/></L>
          <L l="Price USD"><input data-testid="pf-price" required type="number" step="0.01" value={f.price_usd} onChange={e=>setF({...f,price_usd:e.target.value})} className={inp}/></L>
          <L l="Unit label"><input data-testid="pf-unit" value={f.unit_label} onChange={e=>setF({...f,unit_label:e.target.value})} className={inp}/></L>
          <L l="Stock"><input data-testid="pf-stock" type="number" value={f.stock} onChange={e=>setF({...f,stock:e.target.value})} className={inp}/></L>
          <L l="MOQ units"><input data-testid="pf-moq" type="number" value={f.moq_units} onChange={e=>setF({...f,moq_units:e.target.value})} className={inp}/></L>
          <div className="col-span-2"><L l="Image URL"><input data-testid="pf-image" value={f.image_url} onChange={e=>setF({...f,image_url:e.target.value})} className={inp}/></L></div>
          <div className="col-span-2"><L l="Short description"><input data-testid="pf-short" value={f.short_description} onChange={e=>setF({...f,short_description:e.target.value})} className={inp}/></L></div>
          <div className="col-span-2"><L l="Description"><textarea data-testid="pf-desc" rows={3} value={f.description} onChange={e=>setF({...f,description:e.target.value})} className={inp}/></L></div>
          <div className="col-span-2"><L l="Tasting notes (comma separated)"><input data-testid="pf-notes" value={f.tasting_notes} onChange={e=>setF({...f,tasting_notes:e.target.value})} className={inp}/></L></div>
          <div className="col-span-2"><L l="Certifications (comma separated)"><input data-testid="pf-certs" value={f.certifications} onChange={e=>setF({...f,certifications:e.target.value})} className={inp}/></L></div>
          <label className="flex items-center gap-2 text-sm"><input data-testid="pf-featured" type="checkbox" checked={!!f.featured} onChange={e=>setF({...f,featured:e.target.checked})}/> Featured</label>
          <label className="flex items-center gap-2 text-sm"><input data-testid="pf-active" type="checkbox" checked={!!f.active} onChange={e=>setF({...f,active:e.target.checked})}/> Active</label>

          {err && <p data-testid="pf-error" className="col-span-2 text-[#C84B31] text-sm">{String(err)}</p>}
          <div className="col-span-2 flex gap-3 mt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#1A1A1A] py-3 uppercase tracking-overline text-xs hover:bg-[#1A1A1A] hover:text-[#FAF9F6]">Cancel</button>
            <button data-testid="pf-submit" className="flex-1 bg-[#C84B31] text-[#FAF9F6] py-3 uppercase tracking-overline text-xs hover:bg-[#A93F29]">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function L({ l, children }) {
  return <label className="block"><span className="text-[10px] uppercase tracking-overline text-[#7A7A7A] block mb-1">{l}</span>{children}</label>;
}

function OrdersList() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(r => setList(r.data)); }, []);
  return (
    <div>
      <h2 className="font-serif-display text-4xl tracking-tight mb-8">Orders</h2>
      {list.length === 0 ? <p data-testid="admin-no-orders" className="text-[#7A7A7A]">No orders yet.</p> :
        <div className="space-y-4">
          {list.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-[#F5F2EA] border border-[#D5D1C5] p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-serif-display text-2xl">${o.amount.toFixed(2)} {o.currency.toUpperCase()}</p>
                  <p className="text-sm text-[#4A4A4A]">{o.customer_name} · {o.customer_email}</p>
                  <p className="text-xs text-[#7A7A7A] mt-1">{o.shipping_country} · {new Date(o.created_at).toLocaleString()}</p>
                </div>
                <span className="text-xs uppercase tracking-overline bg-[#4A5D23] text-[#FAF9F6] px-3 py-1">{o.status}</span>
              </div>
              <ul className="mt-4 text-sm text-[#4A4A4A] list-disc pl-5">
                {o.line_items.map((li, i) => <li key={i}>{li.name} × {li.quantity} = ${li.subtotal.toFixed(2)}</li>)}
              </ul>
            </div>
          ))}
        </div>}
    </div>
  );
}

function InquiriesList() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/inquiries").then(r => setList(r.data)); }, []);
  return (
    <div>
      <h2 className="font-serif-display text-4xl tracking-tight mb-8">B2B Inquiries</h2>
      {list.length === 0 ? <p data-testid="admin-no-inquiries" className="text-[#7A7A7A]">No inquiries yet.</p> :
        <div className="space-y-4">
          {list.map((q) => (
            <div key={q.id} data-testid={`admin-inquiry-${q.id}`} className="bg-[#F5F2EA] border border-[#D5D1C5] p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-serif-display text-2xl">{q.company_name}</p>
                  <p className="text-sm text-[#4A4A4A]">{q.contact_name} · {q.email} · {q.country}</p>
                  <p className="text-xs text-[#7A7A7A] mt-1">{new Date(q.created_at).toLocaleString()}</p>
                </div>
                <span className="text-xs uppercase tracking-overline border border-[#1A1A1A] px-3 py-1">{q.status}</span>
              </div>
              <p className="mt-4 text-sm text-[#1A1A1A]">
                <strong>{q.product_interest}</strong> · {q.estimated_quantity_kg} kg · {q.incoterm}
                {q.target_price_usd_kg ? ` · target $${q.target_price_usd_kg}/kg` : ""}
                {q.port_of_discharge ? ` · port: ${q.port_of_discharge}` : ""}
              </p>
              {q.message && <p className="mt-2 text-sm text-[#4A4A4A] italic">&ldquo;{q.message}&rdquo;</p>}
            </div>
          ))}
        </div>}
    </div>
  );
}
